import { RACK_SIZE, RANKS, SUITS } from "./constants.js";
import type { Card, MatchEvent, MatchPlayer, MatchState, Rank, Seat } from "./types.js";

const HAND_MODE_CARD_COUNT = 6;

export class GameRuleError extends Error {
  constructor(
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "GameRuleError";
  }
}

export type RandomSource = () => number;

export interface PlayerSeed {
  id: string;
  name: string;
  seat: Seat;
}

function now(): number {
  return Date.now();
}

export function createDeck(copies: 1 | 2 = 1): Card[] {
  return Array.from({ length: copies }, (_, copy) =>
    SUITS.flatMap((suit) =>
      RANKS.map((rank) => ({
        id: `${suit}-${rank}-${copy}`,
        rank,
        suit,
      })),
    ),
  ).flat();
}

export function shuffle<T>(items: readonly T[], random: RandomSource = Math.random): T[] {
  const result = [...items];

  for (let index = result.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    [result[index], result[swapIndex]] = [result[swapIndex], result[index]];
  }

  return result;
}

export function areAdjacent(first: Rank, second: Rank): boolean {
  const distance = Math.abs(first - second);
  return distance === 1 || distance === 12;
}

export function canPlay(card: Card, pileTop: Card | null): boolean {
  return pileTop !== null && areAdjacent(card.rank, pileTop.rank);
}

export function legalPiles(card: Card, match: MatchState): Array<0 | 1> {
  const legal: Array<0 | 1> = [];
  const firstTop = match.piles[0].at(-1) ?? null;
  const secondTop = match.piles[1].at(-1) ?? null;

  if (canPlay(card, firstTop)) legal.push(0);
  if (canPlay(card, secondTop)) legal.push(1);

  return legal;
}

function draw(stock: Card[]): Card | null {
  return stock.pop() ?? null;
}

function stackAt(player: MatchPlayer, index: number): Card[] {
  if (!player.rackStacks[index]) player.rackStacks[index] = [];
  return player.rackStacks[index];
}

export function playerCardCount(player: MatchPlayer): number {
  return (
    player.stock.length +
    player.rack.filter((card) => card !== null).length +
    player.rackStacks.reduce((total, stack) => total + stack.length, 0)
  );
}

/**
 * Returns every personal card in a stable display order. In hand mode all of
 * these cards are legal sources; outside hand mode only the flat rack tops are.
 */
export function playerHandCards(player: MatchPlayer): Card[] {
  const cards: Card[] = [];
  const slotCount = Math.max(player.rack.length, player.rackStacks.length);

  for (let index = 0; index < slotCount; index += 1) {
    const rackCard = player.rack[index];
    if (rackCard) cards.push(rackCard);
    const stack = player.rackStacks[index] ?? [];
    for (let stackIndex = stack.length - 1; stackIndex >= 0; stackIndex -= 1) {
      cards.push(stack[stackIndex]);
    }
  }

  for (let index = player.stock.length - 1; index >= 0; index -= 1) {
    cards.push(player.stock[index]);
  }

  return cards;
}

export function isHandMode(player: MatchPlayer): boolean {
  return player.handMode || playerCardCount(player) <= HAND_MODE_CARD_COUNT;
}

export function playableCards(player: MatchPlayer): Card[] {
  if (isHandMode(player)) return playerHandCards(player);
  return player.rack.flatMap((card) => (card ? [card] : []));
}

function createPlayer(seed: PlayerSeed, cards: Card[]): MatchPlayer {
  const rack = Array.from({ length: RACK_SIZE }, () => draw(cards));

  return {
    ...seed,
    rack,
    rackStacks: Array.from({ length: RACK_SIZE }, () => []),
    stock: cards,
    handMode: false,
    capturedCount: 0,
    chinCount: 0,
    falseChinCount: 0,
  };
}

function topRanksMatch(piles: MatchState["piles"]): boolean {
  const first = piles[0].at(-1);
  const second = piles[1].at(-1);
  return first !== undefined && second !== undefined && first.rank === second.rank;
}

function hasLegalMove(player: MatchPlayer, match: MatchState): boolean {
  return playableCards(player).some((card) => legalPiles(card, match).length > 0);
}

export function isStalled(match: MatchState): boolean {
  if (match.phase !== "playing" || topRanksMatch(match.piles)) return false;
  return !match.players.some((player) => hasLegalMove(player, match));
}

function clonePlayer(player: MatchPlayer): MatchPlayer {
  return {
    ...player,
    rack: [...player.rack],
    rackStacks: player.rackStacks.map((stack) => [...stack]),
    stock: [...player.stock],
  };
}

export function cloneMatch(match: MatchState): MatchState {
  return {
    ...match,
    players: [clonePlayer(match.players[0]), clonePlayer(match.players[1])],
    piles: [[...match.piles[0]], [...match.piles[1]]],
    lastEvent: { ...match.lastEvent },
  };
}

function activateHandMode(player: MatchPlayer): void {
  if (isHandMode(player)) player.handMode = true;
}

function activateHandModes(match: MatchState): void {
  for (const player of match.players) activateHandMode(player);
}

function refillRack(player: MatchPlayer): void {
  if (isHandMode(player)) return;
  for (let index = 0; index < RACK_SIZE; index += 1) {
    if (player.rack[index] === null || player.rack[index] === undefined) {
      player.rack[index] = draw(player.stock);
    }
  }
}

/**
 * Equal visible ranks collapse automatically. The higher slot is folded into
 * the lower slot, including its accumulated cards, and the freed slot is
 * refilled. Replacements can create another pair, so normalization cascades.
 */
function stackEqualRackCards(player: MatchPlayer): void {
  if (isHandMode(player)) return;
  refillRack(player);

  while (true) {
    let targetIndex = -1;
    let sourceIndex = -1;

    for (let first = 0; first < RACK_SIZE && sourceIndex === -1; first += 1) {
      const firstCard = player.rack[first];
      if (!firstCard) continue;
      for (let second = first + 1; second < RACK_SIZE; second += 1) {
        const secondCard = player.rack[second];
        if (secondCard && secondCard.rank === firstCard.rank) {
          targetIndex = first;
          sourceIndex = second;
          break;
        }
      }
    }

    if (sourceIndex === -1) return;

    const sourceCard = player.rack[sourceIndex];
    if (!sourceCard) return;
    const sourceStack = stackAt(player, sourceIndex);
    stackAt(player, targetIndex).push(...sourceStack, sourceCard);
    player.rackStacks[sourceIndex] = [];
    player.rack[sourceIndex] = draw(player.stock);
  }
}

function restartDealer(match: MatchState): MatchPlayer {
  const [first, second] = match.players;
  const firstCount = playerCardCount(first);
  const secondCount = playerCardCount(second);

  if (firstCount > secondCount) return first;
  if (secondCount > firstCount) return second;
  return match.players.find((player) => player.seat === match.stallDealerSeat) ?? first;
}

function updateDerived(match: MatchState): void {
  match.chinAvailable = topRanksMatch(match.piles);
  match.stalled = isStalled(match);
  match.stalledPlayerId = match.stalled ? restartDealer(match).id : null;
}

function finishIfNeeded(match: MatchState): void {
  const emptyPlayers = match.players.filter((player) => playerCardCount(player) === 0);
  if (emptyPlayers.length === 0) return;

  match.phase = "finished";
  match.winnerId = emptyPlayers.length === 1 ? emptyPlayers[0].id : null;
  match.finishedAt = now();
  match.stalled = false;
  match.stalledPlayerId = null;
  match.lastEvent = {
    type: "match-finished",
    at: match.finishedAt,
    winnerId: match.winnerId,
  };
}

function settle(match: MatchState): void {
  if (match.phase !== "playing") return;
  activateHandModes(match);
  for (const player of match.players) stackEqualRackCards(player);
  updateDerived(match);
}

function removeRackTop(player: MatchPlayer, rackIndex: number, refillFromStock: boolean): Card | null {
  const card = player.rack[rackIndex] ?? null;
  if (!card) return null;

  const stack = stackAt(player, rackIndex);
  const revealed = stack.pop() ?? null;
  player.rack[rackIndex] = revealed ?? (refillFromStock ? draw(player.stock) : null);
  return card;
}

function removeHandCard(player: MatchPlayer, cardId: string): Card | null {
  const rackIndex = player.rack.findIndex((card) => card?.id === cardId);
  if (rackIndex !== -1) return removeRackTop(player, rackIndex, false);

  for (let index = 0; index < player.rackStacks.length; index += 1) {
    const stack = player.rackStacks[index];
    const stackIndex = stack.findIndex((card) => card.id === cardId);
    if (stackIndex !== -1) return stack.splice(stackIndex, 1)[0] ?? null;
  }

  const stockIndex = player.stock.findIndex((card) => card.id === cardId);
  if (stockIndex !== -1) return player.stock.splice(stockIndex, 1)[0] ?? null;
  return null;
}

function takeStarter(player: MatchPlayer): Card | null {
  const stockCard = draw(player.stock);
  if (stockCard) return stockCard;

  const rackIndex = player.rack.findIndex((card) => card !== null);
  if (rackIndex !== -1) return removeRackTop(player, rackIndex, false);

  for (const stack of player.rackStacks) {
    const stackedCard = stack.pop();
    if (stackedCard) return stackedCard;
  }
  return null;
}

function seedCenter(match: MatchState): void {
  const first = takeStarter(match.players[0]);
  const second = takeStarter(match.players[1]);

  if (first) match.piles[0].push(first);
  if (second) match.piles[1].push(second);
}

/**
 * Tras un CHIN o un bloqueo, un único jugador reinicia ambos pozos. Lo decide
 * la cantidad total de cartas; si empatan, se alterna el asiento responsable.
 */
function restartCenter(match: MatchState): {
  dealer: MatchPlayer;
  cards: [Card | null, Card | null];
} {
  const tied = playerCardCount(match.players[0]) === playerCardCount(match.players[1]);
  const dealer = restartDealer(match);
  const cards: [Card | null, Card | null] = [
    takeStarter(dealer),
    takeStarter(dealer),
  ];

  if (cards[0]) match.piles[0].push(cards[0]);
  if (cards[1]) match.piles[1].push(cards[1]);
  if (tied) match.stallDealerSeat = dealer.seat === 0 ? 1 : 0;

  return { dealer, cards };
}

export interface MatchOptions {
  decks?: 1 | 2;
}

export function startMatch(
  playerSeeds: [PlayerSeed, PlayerSeed],
  random: RandomSource = Math.random,
  options: MatchOptions = {},
): MatchState {
  const deck = shuffle(createDeck(options.decks ?? 1), random);
  const half = deck.length / 2;
  const firstCards = deck.slice(0, half);
  const secondCards = deck.slice(half);
  const startedAt = now();

  const match: MatchState = {
    phase: "playing",
    revision: 1,
    players: [createPlayer(playerSeeds[0], firstCards), createPlayer(playerSeeds[1], secondCards)],
    piles: [[], []],
    chinAvailable: false,
    stalled: false,
    stalledPlayerId: null,
    stallDealerSeat: 0,
    winnerId: null,
    lastEvent: { type: "match-started", at: startedAt },
    startedAt,
  };

  seedCenter(match);
  settle(match);
  return match;
}

function getPlayer(match: MatchState, playerId: string): MatchPlayer {
  const player = match.players.find((candidate) => candidate.id === playerId);
  if (!player) throw new GameRuleError("PLAYER_NOT_FOUND", "Ese jugador no pertenece a la partida.");
  return player;
}

function assertPlaying(match: MatchState): void {
  if (match.phase !== "playing") {
    throw new GameRuleError("MATCH_NOT_PLAYING", "La partida no está en juego.");
  }
}

export function playCard(
  current: MatchState,
  playerId: string,
  cardId: string,
  pileIndex: 0 | 1,
  expectedTopCardId?: string,
): MatchState {
  assertPlaying(current);
  const match = cloneMatch(current);
  activateHandModes(match);
  const player = getPlayer(match, playerId);
  const card = playableCards(player).find((candidate) => candidate.id === cardId) ?? null;

  if (!card) {
    throw new GameRuleError("CARD_NOT_IN_RACK", "Esa carta ya no está disponible.");
  }

  const pileTop = match.piles[pileIndex].at(-1) ?? null;
  if (expectedTopCardId !== undefined && pileTop?.id !== expectedTopCardId) {
    throw new GameRuleError("STALE_TOP", "La carta del centro cambió antes de tu jugada.");
  }
  if (!canPlay(card, pileTop)) {
    throw new GameRuleError("INVALID_SEQUENCE", "La carta debe ser inmediatamente mayor o menor.");
  }

  const removed = isHandMode(player)
    ? removeHandCard(player, cardId)
    : removeRackTop(player, player.rack.findIndex((candidate) => candidate?.id === cardId), true);
  if (!removed) throw new GameRuleError("CARD_NOT_IN_RACK", "Esa carta ya no está disponible.");

  match.piles[pileIndex].push(removed);
  const playedEvent: MatchEvent = {
    type: "card-played",
    at: now(),
    actorId: playerId,
    card: removed,
    pileIndex,
  };
  match.lastEvent = playedEvent;

  finishIfNeeded(match);
  settle(match);
  if (match.phase === "playing") match.lastEvent = playedEvent;
  match.revision += 1;
  return match;
}

export function claimChin(
  current: MatchState,
  playerId: string,
  random: RandomSource = Math.random,
): MatchState {
  assertPlaying(current);
  const match = cloneMatch(current);
  activateHandModes(match);
  const player = getPlayer(match, playerId);
  const correct = topRanksMatch(match.piles);
  const opponent = match.players.find((candidate) => candidate.id !== playerId);
  if (!opponent) throw new GameRuleError("PLAYER_NOT_FOUND", "Falta el rival de esta partida.");

  const penalizedPlayer = correct ? opponent : player;
  const collectedCards = [...match.piles[0], ...match.piles[1]];
  const capturedCount = collectedCards.length;
  // draw() roba con pop(): unshift conserva primero la reserva que ya tenía.
  penalizedPlayer.stock.unshift(...shuffle(collectedCards, random));
  if (correct) {
    player.capturedCount += capturedCount;
    player.chinCount += 1;
  } else {
    player.falseChinCount += 1;
  }
  match.piles = [[], []];
  const chinEvent: MatchEvent = {
    type: "chin-claimed",
    at: now(),
    actorId: playerId,
    capturedCount,
    penalizedPlayerId: penalizedPlayer.id,
    correct,
  };
  match.lastEvent = chinEvent;

  restartCenter(match);
  finishIfNeeded(match);
  settle(match);
  if (match.phase === "playing") match.lastEvent = chinEvent;
  match.revision += 1;
  return match;
}

export function refreshCenter(current: MatchState): MatchState {
  assertPlaying(current);
  if (!isStalled(current)) {
    throw new GameRuleError("MOVES_AVAILABLE", "Todavía queda al menos una jugada posible.");
  }

  const match = cloneMatch(current);
  activateHandModes(match);
  const { dealer, cards } = restartCenter(match);
  if (!cards[0] && !cards[1]) {
    throw new GameRuleError("NO_CARD_TO_REFRESH", "El jugador designado no tiene cartas para el centro.");
  }

  const refreshEvent: MatchEvent = {
    type: "center-refreshed",
    at: now(),
    actorId: dealer.id,
    cards,
  };
  match.lastEvent = refreshEvent;

  finishIfNeeded(match);
  settle(match);
  if (match.phase === "playing") match.lastEvent = refreshEvent;
  match.revision += 1;
  return match;
}
